import React, { ForwardedRef, useCallback, useEffect, useImperativeHandle, useRef, useState, } from 'react';
import * as echarts from 'echarts';
import { EChartsType } from 'echarts';
import { ChartProps, ChartRef, ILegendItemRect } from './_config/interface';
import { xAxisIntervalFormat } from './_config/plug';
import { getLegendItemName, getLegendItemDOMRect, getZRender } from './hooks/legendZrUtils';

const ChartInner: React.ForwardRefRenderFunction<ChartRef, ChartProps> = (
  { option, hasDataZoom, chartEventConfig, notMerge = true, calculateTotal, getChart, onLegendSelectChange, onLegendMouseover, onLegendMouseout },
  ref: ForwardedRef<ChartRef>
) => {
  const cRef = useRef<HTMLDivElement>(null);
  const cInstance = useRef<EChartsType>();
  const [cache, setCache] = useState('');

  const handleLegendSelectChange = useCallback((event: any) => {
    if (!cInstance.current) return;

    if (calculateTotal) {
      const newOption = calculateTotal(event);
      if (newOption) {
        cInstance.current.setOption(newOption);
      }
    }

    // 获取当前展示的series信息
    if (onLegendSelectChange) {
      const chartOption = cInstance.current.getOption();
      const legend = Array.isArray(chartOption.legend) ? chartOption.legend[0] : chartOption.legend;
      if (legend?.selected) {
        const visibleSeries = Object.keys(legend.selected || {}).filter(key => legend.selected?.[key]);
        onLegendSelectChange(visibleSeries);
      }
    }
  }, [calculateTotal, onLegendSelectChange]);

  // 初始化注册组件，监听 cRef 和 option 变化
  useEffect(() => {
    // 减少 不必要渲染
    if (cache === JSON.stringify(option)) {
      return;
    }
    setCache(JSON.stringify(option));
    if (cRef.current) {
      // 校验 Dom 节点上是否已经挂载了 ECharts 实例，只有未挂载时才初始化
      cInstance.current = echarts.getInstanceByDom(cRef.current);
      if (!cInstance.current) {
        cInstance.current = echarts.init(cRef.current, undefined, {
          renderer: 'canvas',
          devicePixelRatio: 4,
        });
        if (chartEventConfig && cInstance.current) {
          option = chartEventConfig(option, cInstance.current);
        }
      }
      // 设置配置项
      if (option) cInstance.current?.setOption(option, notMerge); // 第二个参数notMerge，默认为false，意为合并之前的option
      getChart?.(cInstance.current);
    }
  }, [cRef, option]);

  // 处理图例选择事件监听
  useEffect(() => {
    const chartInstance = cInstance.current;
    if (!chartInstance || (!calculateTotal && !onLegendSelectChange)) {
      return;
    }

    chartInstance.on('legendselectchanged', handleLegendSelectChange);

    return () => {
      chartInstance.off('legendselectchanged', handleLegendSelectChange);
    };
  }, [handleLegendSelectChange, calculateTotal, onLegendSelectChange]);

  // 通过 ZRender mousemove 检测 legend 项 hover（携带真实 DOM 坐标，绕过 ECharts action 事件限制）
  useEffect(() => {
    const chartInstance = cInstance.current;
    if (!chartInstance || (!onLegendMouseover && !onLegendMouseout)) {
      return;
    }

    const zr = getZRender(chartInstance);
    if (!zr) return;

    let activeLegendName: string | null = null;
    let rafId: number | null = null;

    const onMousemove = (e: any) => {
      if (rafId !== null) return;
      const target = e.target;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        const legendName = getLegendItemName(target, chartInstance);
        if (legendName === activeLegendName) return;
        activeLegendName = legendName;
        if (legendName) {
          let itemDOMRect: ILegendItemRect | undefined;
          try {
            if (cRef.current) {
              itemDOMRect = getLegendItemDOMRect(target, cRef.current);
            }
          } catch (_) { /* 容错：定位失败回退到鼠标坐标 */ }
          onLegendMouseover?.(legendName, e, itemDOMRect);
        } else {
          onLegendMouseout?.();
        }
      });
    };

    const onGlobalout = () => {
      if (activeLegendName) {
        activeLegendName = null;
        onLegendMouseout?.();
      }
    };

    zr.on('mousemove', onMousemove);
    zr.on('globalout', onGlobalout);

    return () => {
      zr.off('mousemove', onMousemove);
      zr.off('globalout', onGlobalout);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [onLegendMouseover, onLegendMouseout]);

  useEffect(() => {
    if (hasDataZoom && cInstance.current) {
      option = xAxisIntervalFormat(option, {
        formatter: (value: any) => {
          return value ? value?.slice(11, 16) : '';
        },
        chart: cInstance.current
      });
      // legendSelectChanged(cInstance.current)
    }
  }, [hasDataZoom, cInstance, JSON.stringify(option)]);

  // 监听窗口大小变化重绘
  useEffect(() => {
    window.addEventListener('resize', resize);
    return () => {
      window.removeEventListener('resize', resize);
    };
  }, [option]);

  // 监听元素宽高变化重绘
  useEffect(() => {
    resize();
  }, [cRef?.current?.clientWidth, cRef?.current?.clientHeight]);

  // 重新适配大小并开启过渡动画
  const resize = () => {
    cInstance.current?.resize({
      animation: { duration: 300 }
    });
  };

  // 获取实例
  const instance = () => {
    return cInstance.current;
  };

  // 对父组件暴露的方法
  useImperativeHandle(ref, () => ({
    instance,
  }));


  return (
    <div ref={cRef} style={{ width: '100%', height: '100%' }} />
  );
};

const Chart = React.forwardRef(ChartInner);

export default Chart;
